import SquareGrid from '@/components/SquareGrid'
import { CBox, RBox } from '@/pages/__styled'
import { editorTheme as theme } from '@/theme'
import { Note, PatternDraft, PatternMeshColor } from '@/types'
import { NoteType } from '@/utils/constants'
import { getAssetName, getAssetThumb, isNumeric, isUniqueColor } from '@/utils/helper'
import { Box, List, Typography } from '@mui/material'
import { styled } from '@mui/system'

interface PatternNotesGroupListProps {
  title: string
  patternNoteType: NoteType
  data: Note[] // sectioned data, notes inside groups
  patternDetails?: PatternDraft
}

export const NoteItemBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '72px',
  height: '72px',
  borderRadius: '12px',
  border: '1px solid rgba(226, 232, 240, 0.8)',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.12), 0 2px 4px rgba(15, 23, 42, 0.08)',
    borderColor: 'rgba(99, 102, 241, 0.3)',
  },
  '&.large': {
    width: '110px',
    height: '110px',
    borderRadius: '14px',
  },

  '&.medium': {
    width: '88px',
    height: '88px',
    borderRadius: '12px',
  },

  '&.small': {
    width: '72px',
    height: '72px',
    borderRadius: '12px',
  },
})

const CardContainer = styled(Box)({
  '& .title': {
    fontWeight: 600,
    fontSize: '13px',
    textTransform: 'capitalize',
    lineHeight: '18px',
    color: '#64748b',
    letterSpacing: '-0.01em',
  },
  '& .titleBold': {
    fontWeight: 700,
    fontSize: '16px',
    textTransform: 'capitalize',
    lineHeight: '20px',
    color: '#0f172a',
    letterSpacing: '-0.02em',
  },
})

const PatternNotesGroupList = ({
  title,
  patternNoteType,
  data,
  patternDetails,
}: PatternNotesGroupListProps) => {
  let colorSwatches: PatternMeshColor[][] = []
  let imagePaths: Set<string> = new Set()
  let assetName = ''
  let borderNoteText: any = ''

  if (patternNoteType == NoteType.BOARDER) {
    borderNoteText = JSON.parse(data[0].noteText)
  }

  if (patternNoteType == NoteType.TEMPLATE) {
    imagePaths.add(getAssetThumb(NoteType.TEMPLATE, patternDetails?.templateID || 0) || '')
    assetName = getAssetName(NoteType.TEMPLATE, patternDetails?.templateID || 0) || ''
  } else if (patternNoteType == NoteType.MATERIAL) {
    Object.values(patternDetails?.materialID || []).forEach((e) => {
      if (e) {
        let path = getAssetThumb(NoteType.MATERIAL, e || 0)
        if (path) {
          imagePaths.add(path)
        }
      }
    })
  }

  if (patternNoteType == NoteType.ACCENT && isNumeric(patternDetails?.accentID)) {
    imagePaths.add(getAssetThumb(NoteType.ACCENT, patternDetails?.accentID || 0) || '')
  } else if (patternNoteType == NoteType.PRINT) {
    Object.values(patternDetails?.print || [])
      .map((e) => e?.id)
      .forEach((x) => {
        if (x) {
          let path = getAssetThumb(NoteType.PRINT, x)
          if (path) {
            imagePaths.add(path)
          }
        }
      })
  } else if (patternNoteType == NoteType.COLOR) {
    Object.values(patternDetails?.color || {}).forEach((e) => {
      if (Array.isArray(e) && e.length > 0) {
        if (isUniqueColor(colorSwatches, e)) {
          colorSwatches.push(e)
        }
      }
    })
  }

  return (
    <CardContainer>
      {colorSwatches.length > 0 ||
      imagePaths.size > 0 ||
      (patternNoteType == NoteType.BOARDER && borderNoteText.width != 0) ? (
        <Box
          display={'flex'}
          flexDirection={'column'}
          sx={{
            background: '#ffffff',
            borderRadius: '16px',
            my: '16px',
            padding: '18px 20px',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.08), 0 2px 4px rgba(15, 23, 42, 0.06)',
              borderColor: 'rgba(99, 102, 241, 0.15)',
            },
          }}
        >
          {patternNoteType != NoteType.TEMPLATE && (
            <Typography className='title' sx={{ mb: '10px' }}>
              {title}
            </Typography>
          )}

          {data?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f5f9',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#cbd5e1',
                  borderRadius: '3px',
                  '&:hover': {
                    backgroundColor: '#94a3b8',
                  },
                },
              }}
            >
              {patternNoteType == NoteType.BOARDER ? (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '20px',
                    flexDirection: 'row',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      backgroundColor: '#f8fafc',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                    }}
                  >
                    <Typography className='titleBold'>{borderNoteText.width * 100}</Typography>
                    <Typography className='title' sx={{ fontSize: '12px' }}>
                      {'Border Size'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      backgroundColor: '#f8fafc',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                    }}
                  >
                    <Typography className='titleBold'>{borderNoteText.type}</Typography>
                    <Typography className='title' sx={{ fontSize: '12px' }}>
                      {'Border Type'}
                    </Typography>
                  </Box>
                </Box>
              ) : patternNoteType == NoteType.COLOR ? (
                <RBox gap={'12px'}>
                  {colorSwatches.map((e, i) => (
                    <Box
                      key={`swatch-${i}`}
                      sx={{
                        padding: '8px',
                        borderRadius: '12px',
                        border: '1px solid rgba(226, 232, 240, 0.8)',
                        backgroundColor: '#fafafa',
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.12)',
                          borderColor: 'rgba(99, 102, 241, 0.3)',
                        },
                      }}
                    >
                      <SquareGrid itemSize={26} gap={3}>
                        {e.map((rgb, rgbIndex) => (
                          <Box
                            key={`color-${i}-${rgbIndex}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '6px',
                              backgroundColor: `rgb(${rgb.r * 255},${rgb.g * 255},${rgb.b * 255})`,
                              border: '1px solid rgba(0, 0, 0, 0.08)',
                              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                            }}
                          />
                        ))}
                      </SquareGrid>
                    </Box>
                  ))}
                </RBox>
              ) : (
                <RBox gap={'12px'} alignItems='center'>
                  {Array.from(imagePaths).map((e, i) => (
                    <NoteItemBox
                      className={
                        patternNoteType == NoteType.TEMPLATE || patternNoteType == NoteType.ACCENT
                          ? 'large'
                          : ''
                      }
                      sx={{
                        backgroundColor:
                          patternNoteType == NoteType.TEMPLATE || patternNoteType == NoteType.ACCENT
                            ? 'rgba(99, 102, 241, 0.05)'
                            : '#fafafa',
                      }}
                      key={i}
                    >
                      <img
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        src={e}
                        alt='image'
                      />
                    </NoteItemBox>
                  ))}
                  {patternNoteType == NoteType.TEMPLATE && (
                    <CBox sx={{ ml: '8px', gap: '4px' }}>
                      <Typography className='titleBold'>{assetName}</Typography>
                      <Typography
                        className='title'
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#94a3b8',
                        }}
                      >
                        {'Template used for this design'}
                      </Typography>
                    </CBox>
                  )}
                </RBox>
              )}
            </Box>
          )}
        </Box>
      ) : null}
    </CardContainer>
  )
}

export default PatternNotesGroupList
